import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, ExternalLink, Info, ReceiptText } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell,
} from 'recharts';

const TOTAL_EXPENSES_BILLIONS = 547.299;

const COLORS = {
  persons: '#A14A3A',
  governments: '#2F6F8F',
  otherTransfers: '#8A6E3E',
  operations: '#4E5965',
  debt: '#6C4C8F',
  carbon: '#5D7C54',
  actuarial: '#9A9A8C',
  ink: '#1a1a2e',
};

const CATEGORIES = [
  {
    key: 'persons',
    label: 'Transfers to persons',
    plainLabel: 'People benefits',
    amount: 131.579,
    share: 24.0,
    color: COLORS.persons,
    description: 'Old Age Security, Guaranteed Income Supplement, EI benefits, children\'s benefits and COVID-era benefit redeterminations.',
    examples: [
      { label: 'Elderly benefits', amount: 80.294 },
      { label: 'Children\'s benefits', amount: 28.574 },
      { label: 'EI and support measures', amount: 24.880 },
      { label: 'COVID income support adjustments', amount: -2.169 },
    ],
  },
  {
    key: 'operations',
    label: 'Operating expenses',
    plainLabel: 'Running Ottawa',
    amount: 130.454,
    share: 23.8,
    color: COLORS.operations,
    description: 'Operating costs of federal departments, agencies, consolidated Crown corporations and other entities.',
    examples: [
      { label: 'Departments, agencies and consolidated entities', amount: 130.454 },
    ],
  },
  {
    key: 'otherTransfers',
    label: 'Other transfer payments',
    plainLabel: 'Other grants & transfers',
    amount: 107.140,
    share: 19.6,
    color: COLORS.otherTransfers,
    description: 'Transfers to Indigenous Peoples, businesses, farmers, students, researchers, international assistance and other programs.',
    examples: [
      { label: 'Other transfer payments', amount: 107.140 },
    ],
  },
  {
    key: 'governments',
    label: 'Transfers to other governments',
    plainLabel: 'Provinces & territories',
    amount: 105.101,
    share: 19.2,
    color: COLORS.governments,
    description: 'Health, social, child care, equalization, territorial financing, community-building and related transfers.',
    examples: [
      { label: 'Health and social program support', amount: 68.979 },
      { label: 'Fiscal arrangements and other transfers', amount: 36.122 },
    ],
  },
  {
    key: 'debt',
    label: 'Public debt charges',
    plainLabel: 'Debt interest',
    amount: 53.410,
    share: 9.8,
    color: COLORS.debt,
    description: 'Interest and related charges on federal debt.',
    examples: [
      { label: 'Public debt charges', amount: 53.410 },
    ],
  },
  {
    key: 'carbon',
    label: 'Pollution pricing proceeds returned',
    plainLabel: 'Carbon proceeds returned',
    amount: 15.595,
    share: 2.8,
    color: COLORS.carbon,
    description: 'Proceeds returned to jurisdictions where federal carbon pollution pricing was collected.',
    examples: [
      { label: 'Returned proceeds', amount: 15.595 },
    ],
  },
  {
    key: 'actuarial',
    label: 'Net actuarial losses',
    plainLabel: 'Pension accounting',
    amount: 4.020,
    share: 0.7,
    color: COLORS.actuarial,
    description: 'Accounting expense related to public sector pensions and other employee and veteran future benefits.',
    examples: [
      { label: 'Net actuarial losses', amount: 4.020 },
    ],
  },
];

function dollars(n) {
  return `$${Number(n).toFixed(2)}`;
}

function billions(n) {
  const sign = n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toFixed(1)}B`;
}

function pct(n) {
  return `${Number(n).toFixed(1)}%`;
}

const tipBox = {
  background: COLORS.ink,
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  padding: '10px 14px',
  fontSize: 13,
  color: '#e8e4da',
  minWidth: 230,
};

const SpendingTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div style={tipBox}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{label}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
        <span style={{ color: 'rgba(232,228,218,0.7)' }}>Out of every $100</span>
        <span className="tabular">{dollars(row.dollars)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginTop: 3 }}>
        <span style={{ color: 'rgba(232,228,218,0.7)' }}>Actual 2024-25 spend</span>
        <span className="tabular">{billions(row.amount)}</span>
      </div>
    </div>
  );
};

function SectionPill({ href, color, label }) {
  return (
    <a href={href} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cream-100/20 text-[13px] text-cream-100/70 hover:border-copper-500 hover:text-copper-400 transition">
      <span className="w-2 h-2 rounded-full inline-block" style={{ background: color }} />
      {label}
    </a>
  );
}

function ReceiptRow({ row, selected, onClick }) {
  return (
    <button
      onClick={() => onClick(row.key)}
      className={`w-full text-left grid grid-cols-[1fr_auto] gap-4 px-4 py-3 border-b border-ink-300/60 transition ${selected ? 'bg-cream-100' : 'hover:bg-cream-100/70'}`}
    >
      <div className="flex items-start gap-3">
        <span className="mt-1.5 w-2.5 h-2.5 rounded-full shrink-0" style={{ background: row.color }} />
        <div>
          <div className="text-[15px] font-medium text-ink-800">{row.plainLabel}</div>
          <div className="text-[12.5px] text-ink-500 mt-0.5">{row.label}</div>
        </div>
      </div>
      <div className="font-mono text-[15px] text-ink-800 tabular">{dollars(row.dollars)}</div>
    </button>
  );
}

function DetailPanel({ category }) {
  return (
    <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-7 md:p-8 h-full">
      <div className="kicker text-copper-600 mb-4">Selected category</div>
      <div className="flex items-start justify-between gap-6">
        <div>
          <h2 className="font-serif text-[34px] leading-tight text-ink-800">{category.plainLabel}</h2>
          <p className="mt-3 text-[14.5px] leading-relaxed text-ink-600 max-w-xl">{category.description}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="font-serif text-[44px] leading-none text-ink-800 tabular">{dollars(category.dollars)}</div>
          <div className="text-[12px] text-ink-500 mt-1">of every $100</div>
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-4">
        <div className="border border-ink-300/60 bg-white/40 rounded-sm p-4">
          <div className="kicker text-ink-500 mb-2">Actual spending</div>
          <div className="font-serif text-[34px] leading-none text-ink-800 tabular">{billions(category.amount)}</div>
          <div className="text-[12px] text-ink-500 mt-1">Fiscal year 2024-25</div>
        </div>
        <div className="border border-ink-300/60 bg-white/40 rounded-sm p-4">
          <div className="kicker text-ink-500 mb-2">Share of total</div>
          <div className="font-serif text-[34px] leading-none text-ink-800 tabular">{pct(category.share)}</div>
          <div className="text-[12px] text-ink-500 mt-1">Total expenses: {billions(TOTAL_EXPENSES_BILLIONS)}</div>
        </div>
      </div>

      <div className="mt-8">
        <div className="kicker text-ink-500 mb-3">What this includes</div>
        <div className="space-y-2">
          {category.examples.map((item) => (
            <div key={item.label} className="flex items-center justify-between gap-4 border-b border-ink-300/50 pb-2 text-[14px]">
              <span className="text-ink-600">{item.label}</span>
              <span className="font-mono tabular text-ink-800">{billions(item.amount)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function FederalSpendingHundred() {
  const [selectedKey, setSelectedKey] = useState('persons');

  const data = useMemo(() => CATEGORIES.map((row) => ({
    ...row,
    dollars: row.share,
  })), []);

  const selected = data.find((row) => row.key === selectedKey) || data[0];
  const totalHundred = data.reduce((sum, row) => sum + row.dollars, 0);

  return (
    <div className="min-h-screen bg-cream-100 text-ink-700">
      <Header />

      <section className="bg-ink-800 text-cream-100 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
          <Link to="/" className="inline-flex items-center gap-1.5 text-[13px] text-cream-100/65 hover:text-copper-500 mb-6 transition">
            <ArrowLeft size={14} /> Home
          </Link>
          <div className="kicker text-copper-500 mb-4">Federal spending</div>
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-end">
            <div>
              <h1 className="font-serif text-[52px] md:text-[76px] leading-[0.96] tracking-tight">
                Where does $100<br />of federal spending go?
              </h1>
              <p className="mt-6 text-[16px] text-cream-100/75 max-w-2xl leading-relaxed">
                Canada spent {billions(TOTAL_EXPENSES_BILLIONS)} in fiscal year 2024-25. This page translates the Public Accounts into a simple receipt: every line below shows how much of each federal spending dollar went to major programs, transfers, operations, debt interest and accounting costs.
              </p>
            </div>
            <div className="bg-white/5 border border-cream-100/15 rounded-sm p-6 backdrop-blur-sm">
              <div className="flex items-center gap-2 text-copper-400 mb-4">
                <ReceiptText size={18} />
                <span className="kicker">Federal receipt</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="font-serif text-[44px] leading-none tabular">$100</div>
                  <div className="text-[12px] text-cream-100/55 mt-1">normalized spending</div>
                </div>
                <div>
                  <div className="font-serif text-[44px] leading-none tabular">2024-25</div>
                  <div className="text-[12px] text-cream-100/55 mt-1">latest audited Public Accounts</div>
                </div>
              </div>
              <div className="mt-5 text-[12.5px] leading-relaxed text-cream-100/55">
                Rounded shares add to {totalHundred.toFixed(1)} because the source reports one-decimal percentages.
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-10">
            <SectionPill href="#receipt" color={COLORS.persons} label="The $100 receipt" />
            <SectionPill href="#breakdown" color={COLORS.governments} label="Category details" />
            <SectionPill href="#method" color={COLORS.debt} label="Method & sources" />
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
        <section id="receipt" className="scroll-mt-8 grid lg:grid-cols-[0.95fr_1.05fr] gap-8 items-stretch">
          <div className="bg-cream-50 border border-ink-300/60 rounded-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-ink-300/60 bg-white/35">
              <div className="kicker text-copper-600">Receipt view</div>
              <div className="mt-1 text-[13.5px] text-ink-500">Click a line to inspect what it includes.</div>
            </div>
            {data.map((row) => (
              <ReceiptRow key={row.key} row={row} selected={selectedKey === row.key} onClick={setSelectedKey} />
            ))}
            <div className="px-4 py-4 flex items-center justify-between bg-ink-800 text-cream-100">
              <span className="font-medium">Rounded total</span>
              <span className="font-mono tabular">${totalHundred.toFixed(2)}</span>
            </div>
          </div>

          <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-5 md:p-7">
            <div className="kicker text-copper-600 mb-4">Visual split</div>
            <div className="h-[390px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} layout="vertical" margin={{ top: 8, right: 26, bottom: 8, left: 104 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(47,55,66,0.15)" horizontal={false} />
                  <XAxis type="number" domain={[0, 26]} tickFormatter={(v) => `$${v}`} tick={{ fontSize: 12, fill: '#6F7B87' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="plainLabel" tick={{ fontSize: 12, fill: '#2F3742' }} width={112} axisLine={false} tickLine={false} />
                  <Tooltip content={<SpendingTooltip />} cursor={{ fill: 'rgba(200,137,74,0.08)' }} />
                  <Bar dataKey="dollars" radius={[0, 4, 4, 0]}>
                    {data.map((entry) => <Cell key={entry.key} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        <section id="breakdown" className="scroll-mt-8 mt-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-8 items-stretch">
          <DetailPanel category={selected} />

          <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-7 md:p-8">
            <div className="kicker text-copper-600 mb-4">Share of total expenses</div>
            <div className="h-[310px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data} dataKey="dollars" nameKey="plainLabel" cx="50%" cy="50%" innerRadius={72} outerRadius={112} paddingAngle={1}>
                    {data.map((entry) => <Cell key={entry.key} fill={entry.color} />)}
                  </Pie>
                  <Tooltip content={<SpendingTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 mt-2">
              {data.map((row) => (
                <button key={row.key} onClick={() => setSelectedKey(row.key)} className="w-full flex items-center justify-between gap-4 text-left text-[13.5px] border-b border-ink-300/50 pb-2 hover:text-copper-700 transition">
                  <span className="flex items-center gap-2 text-ink-600">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: row.color }} />
                    {row.plainLabel}
                  </span>
                  <span className="font-mono tabular text-ink-800">{pct(row.share)}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-8 grid md:grid-cols-3 gap-4">
          <div className="bg-ink-800 text-cream-100 rounded-sm p-6">
            <div className="kicker text-copper-500 mb-3">Largest line</div>
            <div className="font-serif text-[36px] leading-none tabular">$24.00</div>
            <p className="mt-3 text-[13px] text-cream-100/65 leading-relaxed">of each $100 went to transfers to persons, led by elderly benefits.</p>
          </div>
          <div className="bg-ink-800 text-cream-100 rounded-sm p-6">
            <div className="kicker text-copper-500 mb-3">Debt interest</div>
            <div className="font-serif text-[36px] leading-none tabular">$9.80</div>
            <p className="mt-3 text-[13px] text-cream-100/65 leading-relaxed">went to public debt charges, equal to {billions(53.410)} in 2024-25.</p>
          </div>
          <div className="bg-ink-800 text-cream-100 rounded-sm p-6">
            <div className="kicker text-copper-500 mb-3">Government transfers</div>
            <div className="font-serif text-[36px] leading-none tabular">$19.20</div>
            <p className="mt-3 text-[13px] text-cream-100/65 leading-relaxed">went to provinces, territories and municipalities for health, social programs, fiscal arrangements and other transfers.</p>
          </div>
        </section>

        <section id="method" className="scroll-mt-8 mt-8 bg-cream-50 border border-ink-300/60 rounded-sm p-7 md:p-8">
          <div className="flex items-start gap-3">
            <Info size={18} className="text-copper-600 mt-1 shrink-0" />
            <div>
              <div className="kicker text-copper-600 mb-3">Method & sources</div>
              <h2 className="font-serif text-[32px] leading-tight text-ink-800">How the $100 is calculated</h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-ink-600 max-w-3xl">
                The calculation divides each major expense category by total federal expenses for fiscal year 2024-25, then scales the result to $100. The primary source is the Government of Canada's Annual Financial Report and Public Accounts for 2024-25. The source reports total expenses of {billions(TOTAL_EXPENSES_BILLIONS)} and classifies expenses into transfers to persons, transfers to other governments, pollution pricing proceeds returned, other transfer payments, operating expenses, public debt charges and net actuarial losses.
              </p>
              <div className="mt-6 grid md:grid-cols-2 gap-4">
                <a href="https://www.canada.ca/en/department-finance/services/publications/annual-financial-report/2025.html" target="_blank" rel="noreferrer" className="border border-ink-300/60 rounded-sm p-4 hover:bg-cream-100 transition">
                  <div className="flex items-center gap-2 font-medium text-ink-800">Annual Financial Report 2024-25 <ExternalLink size={14} /></div>
                  <div className="text-[12.5px] text-ink-500 mt-1">Department of Finance Canada — expense composition and Table 5.</div>
                </a>
                <a href="https://www.canada.ca/en/public-services-procurement/services/payments-accounting/public-accounts/2025.html" target="_blank" rel="noreferrer" className="border border-ink-300/60 rounded-sm p-4 hover:bg-cream-100 transition">
                  <div className="flex items-center gap-2 font-medium text-ink-800">Public Accounts of Canada 2025 <ExternalLink size={14} /></div>
                  <div className="text-[12.5px] text-ink-500 mt-1">Receiver General for Canada — audited financial statements and datasets.</div>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
